import { CustomerType } from "@/graphql/__generated__/graphql";
import { UseFormReturn } from "react-hook-form";
import { z } from "zod";
import {
  DateInputField,
  HiddenInputField,
  InputField,
  RadioInputField,
} from "@/components/shared/form";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import OperationalOrganizationsFormGroup from "../../Shared/FormGroups/OperationalOrganizationsFormGroup";
import { CustomerNameComponent } from "../../Shared/CustomerNameComponent";
import { createDateSchema } from "@/utils/form";

export const stringToNumber = (val: unknown) => (typeof val === "string" ? parseFloat(val) : val);

export const createQuickChanceSchema = (t: TFunction) => {
  return z.object({
    quick: z.object({
      description: z.object({
        name: z.string().min(2, {
          message: t("schemas:createtender.description.namemin"),
        }).max(75, {
          message: t("schemas:createtender.description.namemax"),
        }),
        customerName: z.string().min(2, {
          message: t("schemas:createtender.description.customernamemin"),
        }).optional().or(z.literal("")),
        customerId: z.number().optional().or(z.literal(NaN)),
        customerType: z.enum([CustomerType.Existingcustomer, CustomerType.Newservice, CustomerType.Newcustomer], { // TODO: CustomerType.ExistingCustomerNewSite, Waiting for Backend
          message: t("schemas:createtender.description.customertyperequired"),
        }),
        ownerId: z.number(),
      }).check((ctx) => {
        if (!ctx.value.customerName && !ctx.value.customerId) {
          ctx.issues.push({
            code: "custom",
            path: ["customerName"],
            message: t("schemas:createtender.description.customerrequired"),
            input: ctx.value.customerName,
          });
        }
      }),
      deadline: z.object({
        submission: createDateSchema(t),
      }),
      contract: z.object({
        startDate: createDateSchema(t),
        endDate: createDateSchema(t),
      }).check((ctx) => {
        if (ctx.value.endDate.getTime() < ctx.value.startDate.getTime()) {
          ctx.issues.push({
            code: "custom",
            path: ["endDate"],
            message: t("schemas:createtender.contract.enddateafterstartdate"),
            input: ctx.value.endDate,
          });
        }
      }),
    })
  });
};

export const quickChanceDefaultValues = {
  quick: {
    description: {
      name: "",
      customerName: "",
      customerId: undefined,
      customerType: undefined,
      ownerId: undefined,
    },
    deadline: {
      submission: undefined,
    },
    contract: {
      startDate: undefined,
      endDate: undefined,
    }
  }
};

export function QuickChanceFormGroup({ form }: { form: UseFormReturn<any> }) {
  // Hooks
  const { t } = useTranslation();

  return (
    <div>
      <div className="form-field-container">
        <InputField
          form={form}
          name="quick.description.name"
          label={t("newtender.formgroups.description.inputs.name.label")}
          placeholder={t("newtender.formgroups.description.inputs.name.placeholder")}
          description={t("newtender.formgroups.description.inputs.name.description")}
        />

        <OperationalOrganizationsFormGroup form={form} />

        <CustomerNameComponent
          form={form}
          formPrefix="quick.description"
          className="md:w-[48%]"
        />

        <HiddenInputField form={form} name="customerId" />

        <RadioInputField
          form={form}
          name="quick.description.customerType"
          label={t("newtender.formgroups.description.inputs.customertype.label")}
          options={[
            { value: CustomerType.Existingcustomer, label: t("newtender.formgroups.description.inputs.customertype.options.existingcustomer") },
            // TODO: CustomerType.ExistingCustomerNewSite, Waiting for Backend
            { value: CustomerType.Newservice, label: t("newtender.formgroups.description.inputs.customertype.options.existingandnewsite") },
            { value: CustomerType.Newcustomer, label: t("newtender.formgroups.description.inputs.customertype.options.newcustomer") },
          ]}
        />

        <DateInputField
          form={form}
          name="quick.deadline.submission"
          label={t("newtender.formgroups.deadline.inputs.submission.label")}
        />

        <div className="flex w-full gap-4">
          {/* Start Date */}
          <DateInputField
            form={form}
            name="quick.contract.startDate"
            label={t("newtender.formgroups.contract.inputs.startdate.label")}
          />

          {/* End Date */}
          <DateInputField
            form={form}
            name="quick.contract.endDate"
            label={t("newtender.formgroups.contract.inputs.enddate.label")}
          />
        </div>
      </div>
    </div>
  );
}
